import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { P } from '@/design-system/palette';
import { sans } from '@/design-system/typography';
import { Modal, Btn } from '@/design-system';
import type { Objective } from '@/engine/types';

interface CreateScenarioModalProps {
  open: boolean;
  onClose: () => void;
  onCreate: (name: string) => void;
  existingNames: string[];
  objectives: Objective[];
}

export function CreateScenarioModal({
  open, onClose, onCreate, existingNames, objectives,
}: CreateScenarioModalProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');

  const trimmed = name.trim();
  const isDuplicate = existingNames.some(n => n.toLowerCase() === trimmed.toLowerCase());
  const isValid = trimmed.length > 0 && !isDuplicate;

  const activeObjectives = objectives.filter(o => o.state === 'active');

  const handleCreate = () => {
    if (!isValid) return;
    onCreate(trimmed);
    setName('');
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={() => { setName(''); onClose(); }}
      title={t('budget.scenario.create.title')}
      actions={
        <>
          <Btn variant="secondary" label={t('actions.cancel')} onClick={() => { setName(''); onClose(); }} />
          <Btn variant="primary" label={t('actions.create')} onClick={handleCreate} disabled={!isValid} />
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div>
          <label style={{
            fontFamily: sans, fontSize: 11, fontWeight: 600,
            color: P.t2, display: 'block', marginBottom: 4,
          }}>
            {t('budget.scenario.create.name')}
          </label>
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder={t('budget.scenario.create.namePlaceholder')}
            autoFocus
            onKeyDown={e => { if (e.key === 'Enter' && isValid) handleCreate(); }}
            style={{
              width: '100%', height: 36, padding: '0 10px',
              fontFamily: sans, fontSize: 13, color: P.t1,
              background: P.s2, border: `1px solid ${P.border}`,
              borderRadius: 6, outline: 'none', boxSizing: 'border-box',
            }}
          />
          {isDuplicate && (
            <span style={{ fontFamily: sans, fontSize: 10, color: P.neg, marginTop: 4, display: 'block' }}>
              {t('budget.scenario.create.nameUnique')}
            </span>
          )}
        </div>

        <div style={{
          padding: '10px 12px', background: P.s2, borderRadius: 6,
          fontFamily: sans, fontSize: 11, color: P.t2, lineHeight: 1.5,
        }}>
          {activeObjectives.length > 0
            ? t('budget.scenario.create.objectivesProjected', { count: activeObjectives.length })
            : t('budget.scenario.create.noObjectives')
          }
          {activeObjectives.length > 0 && (
            <div style={{ marginTop: 4 }}>
              {activeObjectives.map(o => (
                <div key={o.id} style={{ color: P.t3, fontSize: 10 }}>• {o.name}</div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
